// ITER287 — Floating Category Selector (Zomato/Swiggy pattern).
//
// UX:
//   - Fixed pill button at bottom-right of the viewport, always visible.
//   - Shows the CURRENT active category name so the guest knows where
//     they are in a long menu.
//   - Tap → bottom sheet slides up listing every category with the live
//     available-item count.
//   - Tap a row → smooth-scrolls to that section, closes the sheet.
//   - Backdrop / Escape closes the sheet without navigation.
//
// No branding is copied — visuals derive from `theme` (outlet colors).
import React, { useEffect } from 'react';
import { X, Menu as MenuIcon } from 'lucide-react';

export function FloatingCategoryFAB({
  categories,          // [{ id, name, count }]
  activeId,
  onSelect,            // (id) => void
  theme,
  open,
  onOpenChange,        // (bool) => void
}) {
  // Escape closes the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onOpenChange(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  // Lock background scroll while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const active = categories.find((c) => c.id === activeId);
  const totalItems = categories.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <>
      {/* FAB — always visible, bottom-right */}
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        data-testid="menu-fab"
        className="fixed bottom-6 right-5 sm:right-8 z-40 flex items-center gap-2
                   rounded-full shadow-lg px-4 h-12 text-sm font-semibold
                   transition-transform active:scale-95"
        style={{
          background: theme.primary,
          color: '#FFF',
          boxShadow: '0 8px 22px rgba(20,61,48,0.25)',
        }}
        aria-label="Open menu categories"
      >
        <MenuIcon className="w-4 h-4" />
        <span className="hidden xs:inline">Menu</span>
        <span className="opacity-80 text-[11px] font-medium hidden sm:inline">
          · {active ? active.name : `${totalItems} items`}
        </span>
      </button>

      {/* Backdrop + Bottom sheet */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
          data-testid="menu-fab-sheet"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 animate-fade-in"
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <div
            className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl
                       shadow-xl animate-slide-up max-h-[80vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Menu categories"
          >
            <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b"
                 style={{ borderColor: `${theme.secondary}33` }}>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold"
                     style={{ color: theme.secondary }}>
                  Jump to
                </div>
                <div className="font-serif text-lg" style={{ color: theme.primary }}>
                  Menu Categories
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="menu-fab-close"
                className="p-2 -mr-2 rounded-full hover:bg-black/5"
                aria-label="Close"
              >
                <X className="w-5 h-5" style={{ color: theme.primary }} />
              </button>
            </div>

            <ul className="overflow-y-auto overscroll-contain divide-y"
                style={{ borderColor: `${theme.secondary}22` }}>
              {categories.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(c.id)}
                      data-testid={`menu-fab-cat-${c.id}`}
                      className="w-full flex items-center justify-between px-5 py-3.5 text-left
                                 transition-colors hover:bg-black/[0.03]"
                      style={{
                        background: isActive ? `${theme.primary}0D` : 'transparent',
                      }}
                    >
                      <span
                        className="text-sm"
                        style={{
                          color: theme.primary,
                          fontWeight: isActive ? 600 : 400,
                        }}
                      >
                        {c.name}
                      </span>
                      <span
                        className="text-xs tabular-nums"
                        style={{
                          color: isActive ? theme.primary : '#9CA3AF',
                          fontWeight: isActive ? 600 : 400,
                        }}
                        data-testid={`menu-fab-cat-count-${c.id}`}
                      >
                        {c.count}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Local keyframes (Tailwind-free animation) */}
      <style>{`
        @keyframes menu-fab-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes menu-fab-slide-up { from { transform: translateY(20%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .animate-fade-in { animation: menu-fab-fade-in 180ms ease-out; }
        .animate-slide-up { animation: menu-fab-slide-up 220ms ease-out; }
      `}</style>
    </>
  );
}

export default FloatingCategoryFAB;
